import React, { useRef, useState } from "react";
import {
  BehaviorSubject,
  catchError,
  auditTime,
  tap,
  of,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  filter,
  takeUntil,
} from "rxjs";
import PropTypes from "prop-types";
import xstream from "state-x-stream";
import { Modal } from "../../../../components/atoms";
import getApiClient from "../../apiClient";
import Region from "../../../../models/Region";
import Autocomplete from "../../../../components/atoms/Autocomplete";
import PositionOption from "./PositionOption";
import PositionPickerIcon from "./PositionPickerIcon";

function controller({ props$, destroyed$ }) {
  const currentPosStr$ = new BehaviorSubject("");
  const inputPosition$ = new BehaviorSubject(null);
  const searchToken$ = new BehaviorSubject("");
  const isLoading$ = new BehaviorSubject(false);
  const { searchGenes } = getApiClient();

  function getGenome() {
    return props$.value.zoomService?.genome;
  }

  function processGenes(query, genes) {
    const genome = getGenome();
    return genes
      .map((gene) => {
        const { chromosome, start, end, name } = gene;
        const chr = genome.getChrById(chromosome);
        if (!chr) return null;
        const region = new Region(start, end, chr);
        const symbol = name.toLowerCase();
        const mInd = symbol.indexOf(query);
        const isGene = true;
        const chrName = region.chromosome.name;
        const pos = `${chrName}:${region.x0}..${region.x1}`;
        const key = name;
        const label = name;
        return { label, region, mInd, isGene, key, pos, chrName };
      })
      .filter((x) => x && x.mInd >= -1)
      .sort((a, b) => a.mInd - b.mInd || a.label.length - b.label.length);
  }

  function errorHandler(e) {
    console.error(e);
    return of([]);
  }

  function shouldAllowGeneSearch(q = "") {
    if (q.length < 3) return false;
    if (q.includes(":")) return false;
    if (q.includes(".")) return false;
    if (q === "chr") return false;
    return true;
  }

  function apiGeneSearch(query) {
    const { genomeId } = getGenome() || {};
    if (!genomeId) return of([]);
    return searchGenes({ query, genomeId }).pipe(
      map((genes) => processGenes(query, genes)),
      catchError(errorHandler)
    );
  }

  function parse(str) {
    try {
      const key = str;
      const label = str;
      const pos = str;
      const shouldSearch = shouldAllowGeneSearch(str);
      const range = props$.value.zoomService.parsePositionStr(str);
      const error = null;
      return { ...range, shouldSearch, label, pos, key, error };
    } catch (e) {
      const shouldSearch = shouldAllowGeneSearch(str);
      const key = str;
      const pos = str;
      const label = str;
      const error = e.message;
      return { pos, error, label, key, shouldSearch };
    }
  }

  function submit(option) {
    const { pos: str } = option || {};
    const { zoomService: zs } = props$.value;
    const range = parse(str || "");
    if (range.error) return;
    const { d1, d2 } = zs.xScale$.value;
    const { start, end } = range;
    const isOutofCurrentRange = start < d1 || end > d2;
    if (isOutofCurrentRange) zs.setIsWholeGenome(true);
    zs.jumpTo(start, end);
  }

  searchToken$
    .pipe(
      distinctUntilChanged(),
      map((x) => parse(x)),
      tap((x) => inputPosition$.next(x)),
      takeUntil(destroyed$)
    )
    .subscribe();

  props$
    .pipe(
      switchMap((x) => x.zoomService.position$),
      auditTime(1000),
      map(() => props$.value.zoomService.getPositionStr()),
      tap((x) => currentPosStr$.next(x)),
      tap((x) => searchToken$.next(x)),
      takeUntil(destroyed$)
    )
    .subscribe();

  return {
    isLoading: isLoading$,
    inputPosition: inputPosition$,
    setSearchToken: (x) => searchToken$.next(x.toLowerCase()),
    currentPosStr: currentPosStr$,
    token: searchToken$,
    chrs: props$.pipe(map((x) => x?.zoomService?.genome?.chrs || [])),
    genes: searchToken$.pipe(
      distinctUntilChanged(),
      filter((x) => shouldAllowGeneSearch(x)),
      tap(() => isLoading$.next(true)),
      debounceTime(700),
      switchMap((query) => apiGeneSearch(query)),
      tap(() => isLoading$.next(false))
    ),
    filteredChrs: searchToken$.pipe(
      distinctUntilChanged(),
      debounceTime(100),
      filter((x) => shouldAllowGeneSearch(x)),
      map((q) => getGenome().chrs.filter((c) => c.lowercaseName.includes(q)))
    ),
    submit,
  };
}

function PositionSearch({
  currentPosStr,
  setSearchToken,
  themeCl,
  isLoading,
  genes,
  chrs,
  filteredChrs,
  submit,
  inputPosition,
  token,
}) {
  const inputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const noResWarning = isLoading ? "searching..." : "";
  const noResError = isLoading ? "" : "no results";
  const noResKey = new Date().getTime();
  const noResults = { warning: noResWarning, error: noResError, key: noResKey };
  const { error, shouldSearch: isSearch } = inputPosition;
  const options = findOptions();

  function findOptions() {
    const hasGenes = genes.length;
    const hasChrs = filteredChrs.length;
    if (isSearch && !hasGenes && !hasChrs) return [noResults, ...chrs];
    if (isSearch) return [...genes, ...filteredChrs];
    if (error && inputPosition?.label?.length < 4) return chrs;
    if (error) return [inputPosition, ...chrs];
    if (hasGenes) return [inputPosition, ...genes];
    return [inputPosition, ...chrs];
  }

  function clear() {
    if (!inputRef.current) return;
    setSearchToken("");
    inputRef.current.focus();
  }

  return (
    <div className="tw-relative">
      <Autocomplete
        className="color-3 tw-px-1 tw-h-6 focus:tw-outline-none bg-1 tw-opacity-80"
        onSelect={(x) => submit(x)}
        options={options}
        optionKey="key"
        OptionContent={PositionOption}
        OptionsWrapper={({ children }) => (
          <div className={themeCl}>
            <div className="bg-1 prevent-lose-focus color-1 tw-max-h-66 tw-absolute tw-top-0 shadow-2xl tw-rounded-lg tw-mt-2 tw-overflow-auto tw-border border-1">
              {children}
            </div>
          </div>
        )}
      >
        <div className="tw-flex tw-items-center tw-relative">
          <input
            className="color-1 tw-px-1 tw-py-0.5 tw-pr-6 tw-rounded-lg tw-border border-2 tw-text-sm tw-w-24 bg-1 sm:tw-w-24 md:tw-w-64"
            value={token}
            onKeyDown={(e) => e.code === "Enter" && submit(options[0])}
            onChange={({ target }) => setSearchToken(target.value)}
            placeholder="chr:start..end or gene symbol"
            ref={inputRef}
          />
        </div>
      </Autocomplete>
      <PositionPickerIcon
        clear={() => clear()}
        isSame={currentPosStr === token}
        hasError={Boolean(options[0]?.error)}
        isLoading={Boolean(isLoading)}
        submit={() => submit(options[0])}
        openModal={() => setIsModalOpen(true)}
        isEmpty={token.length < 3}
        className="show-parent-focus"
      />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        ContentRender={() => {
          return (
            <div className="tw-min-w-72">
              <div className="tw-text-base tw-m-4">Example Searches</div>
              <ul className="tw-mr-6 tw-mb-6">
                <li className="tw-my-2">
                  <b>TP53</b>
                  <span className="tw-ml-2">to search for TP53 gene</span>
                </li>
                <li className="tw-my-2">
                  <b>chr3</b>
                  <span className="tw-ml-2">to search for chromosome 3</span>
                </li>
                <li className="tw-my-2">
                  <b>2:179390716..179672150</b>
                  <span className="tw-ml-2">
                    to go to region between 179,390Mb and 179,672Mb on
                    chromosome 2
                  </span>
                </li>
                <li className="tw-my-2">
                  <b>2:1..6:1 </b>
                  <span className="tw-ml-2">
                    to go to region from chromosome 2 to chromosome 5
                  </span>
                </li>
              </ul>
            </div>
          );
        }}
      />
    </div>
  );
}

const OptionType = PropTypes.shape({
  label: PropTypes.string,
  shouldSearch: PropTypes.bool,
  error: PropTypes.string,
  region: PropTypes.instanceOf(Region),
});

PositionSearch.propTypes = {
  currentPosStr: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  inputPosition: OptionType,
  setSearchToken: PropTypes.func.isRequired,
  themeCl: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  genes: PropTypes.arrayOf(OptionType),
  chrs: PropTypes.arrayOf(OptionType),
  filteredChrs: PropTypes.arrayOf(OptionType),
};

PositionSearch.defaultProps = {
  chrs: [],
  inputPosition: {},
  genes: [],
  filteredChrs: [],
};

export default xstream(controller)(PositionSearch);
